import { TypedEmitter } from "../network/EventEmitter";

/**
 * Per Part 3 (M16): "the StateManager finite-state machine (boot ->
 * unauthenticated -> authenticating -> character_select -> connecting ->
 * in_world -> reconnecting -> error)".
 *
 * This is the single source of truth for "what screen/mode is the client
 * in" — scenes read it to decide what to render and call `transition()` to
 * move it forward; nothing mutates state directly.
 */
export type ClientState =
  | "boot"
  | "unauthenticated"
  | "authenticating"
  | "character_select"
  | "connecting"
  | "in_world"
  | "reconnecting"
  | "error";

type StateManagerEvents = {
  change: { from: ClientState; to: ClientState };
}

/**
 * Explicit legal-transition table. An edge not listed here is refused —
 * this is deliberate: it's what stops e.g. a stray "character selected"
 * event from a leftover listener silently kicking the client from
 * `in_world` back to `character_select` mid-session.
 */
const LEGAL_TRANSITIONS: Record<ClientState, ClientState[]> = {
  boot: ["unauthenticated", "authenticating", "error"], // authenticating: a cached-in-memory-only token from a very recent page action; error: manifest/boot failure path
  unauthenticated: ["authenticating"],
  authenticating: ["character_select", "unauthenticated", "error"],
  character_select: ["connecting", "unauthenticated"],
  connecting: ["in_world", "character_select", "error"],
  in_world: ["reconnecting", "character_select", "error"],
  reconnecting: ["in_world", "character_select", "error"],
  error: ["unauthenticated", "boot"],
};

export class StateManager {
  private state: ClientState = "boot";
  private readonly emitter = new TypedEmitter<StateManagerEvents>();
  private readonly history: ClientState[] = ["boot"];

  get current(): ClientState {
    return this.state;
  }

  canTransition(to: ClientState): boolean {
    return LEGAL_TRANSITIONS[this.state].includes(to);
  }

  /**
   * Throws on an illegal transition rather than silently no-op'ing or
   * clamping — an illegal transition attempt is a bug in the calling
   * scene, and failing loudly in dev is much cheaper than debugging a
   * client that's mysteriously stuck on the wrong screen in production.
   */
  transition(to: ClientState): void {
    if (!this.canTransition(to)) {
      throw new Error(`Illegal state transition: ${this.state} -> ${to}`);
    }
    const from = this.state;
    this.state = to;
    this.history.push(to);
    this.emitter.emit("change", { from, to });
  }

  on(listener: (payload: { from: ClientState; to: ClientState }) => void): () => void {
    return this.emitter.on("change", listener);
  }

  /** Read-only history, useful for debugging/error reporting. */
  getHistory(): readonly ClientState[] {
    return this.history;
  }
}
